import React, { useEffect, useState } from 'react';

import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import { IconTooltip } from '../../../components/common/Tooltip/IconTooltip';
import useLocalStorage from '../../../hooks/useLocalStorage';
import HttpClient from '../../../utils/httpClient';
import { getUrls } from '../../../utils/url';
import AdminAuthContainer from '../AdminAuthContainer';

const Subheader = styled.div`
    color: ${(props) => props.theme.colors.secondary.main};
    margin-bottom: 1rem;
`;

const ProgressText = styled.div`
    margin-bottom: 1rem;
`;

const Container = styled.div`
    margin-bottom: 2rem;
`;

const Title = styled.div`
    font-size: 24px;
    margin-bottom: 1rem;
`;

const StyledButton = styled(LoadingButton)`
    svg {
        color: ${(props) => props.theme.colors.white};
    }
`;

const GlobalUserSyncTool: React.FC = () => {
    const { t } = useTranslation('admin_settings');
    const { uuid } = useContainer(AdminAuthContainer);
    const { enqueueSnackbar } = useSnackbar();
    const [lockout, setLockout] = useLocalStorage('cfz_global_user_sync_lock', 0);

    const [isLoading, setIsLoading] = useState(false);
    const [isLockedOut, setIsLockedOut] = useState(false);
    const [error, setError] = useState(false);

    useEffect(() => {
        const now = Date.now();
        const lockoutDuration = 60 * 60 * 1000; // 1 hour
        if (lockout && now - lockout < lockoutDuration) {
            setIsLockedOut(true);
        } else {
            setIsLockedOut(false);
        }
    }, [lockout]);

    /**
     * Pre-warms the global sync lambda when loading the tool
     */
    useEffect(() => {
        const { api } = getUrls();
        const url = `${api}/v1/users/global-sync?noop=1`;

        const httpClient = new HttpClient();
        httpClient.setHeader('token', uuid);

        httpClient.post(url);
    }, [uuid]);

    const sync = async () => {
        setIsLoading(true);

        const { api } = getUrls();
        const url = `${api}/v1/users/global-sync`;

        const httpClient = new HttpClient({ timeout: 30000 });
        httpClient.setHeader('token', uuid);

        try {
            await httpClient.post(url);
            enqueueSnackbar(t('global-user-sync-success'), { variant: 'success' });
        } catch (err) {
            console.error(err);
            setError(true);
            enqueueSnackbar(t('global-user-sync-error'), { variant: 'error' });
        }

        setIsLoading(false);
        setIsLockedOut(true);
        setLockout(Date.now());
    };

    return (
        <Container>
            <Title>{t('global-user-sync-title')}</Title>
            <Subheader>{t('global-user-sync-desc')}</Subheader>
            {isLoading && <ProgressText>{t('global-user-sync-loading')}</ProgressText>}
            <StyledButton
                loading={isLoading}
                variant="contained"
                onClick={() => sync()}
                disabled={isLockedOut || error}
            >
                {t('global-user-sync-action')}
            </StyledButton>
            {isLockedOut && <IconTooltip>{t('global-user-sync-lockout')}</IconTooltip>}
        </Container>
    );
};

export default GlobalUserSyncTool;
